import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  User, Users, UsersThree, Brain, ArrowClockwise, Receipt, IdentificationBadge,
} from "@phosphor-icons/react";

const KIND_META = [
  { key: "coach_subscription", title: "Jaarabonnement", icon: IdentificationBadge, color: "#1E88E5" },
  { key: "coachees",            title: "Coachees",       icon: Users,               color: "#43A047" },
  { key: "breintype_tests",     title: "Breintype-tests",icon: Brain,               color: "#E53935" },
];

export default function CompanyBillingPage() {
  const { companyId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [company, setCompany] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchList = async () => {
    setLoading(true);
    setError("");
    try {
      const [cRes, sRes] = await Promise.all([
        api.get(`/companies/${companyId}`),
        api.get(`/companies/${companyId}/billing/summary`),
      ]);
      setCompany(cRes.data);
      setRows(sRes.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => {
    const t = Object.fromEntries(
      KIND_META.map((m) => [m.key, { total: 0, outstanding: 0, invoiced: 0 }]),
    );
    for (const r of rows) {
      for (const m of KIND_META) {
        const c = r[m.key];
        if (!c) continue;
        t[m.key].total += c.total;
        t[m.key].outstanding += c.outstanding;
        t[m.key].invoiced += c.invoiced;
      }
    }
    return t;
  }, [rows]);

  return (
    <AppLayout
      title="Facturatie per work coach"
      breadcrumbs={[
        { label: isAdmin ? "Admin" : "Bedrijf" },
        { label: company?.name || "Bedrijf", to: `/companies/${companyId}` },
        { label: "Facturatie" },
      ]}
    >
      <div data-testid="company-billing-page">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <p className="text-sm text-zinc-600 max-w-2xl">
            {isAdmin
              ? "Per work coach binnen dit bedrijf zie je het aantal factureerbare items. Ga naar Admin › Facturatie om items te markeren."
              : "Per work coach binnen jouw bedrijf zie je het aantal factureerbare items. De administratie loopt via de platform-beheerder."}
          </p>
          <button
            onClick={fetchList}
            data-testid="company-billing-refresh"
            className="inline-flex items-center gap-2 border border-zinc-300 text-zinc-700 hover:bg-zinc-900 hover:text-white hover:border-zinc-900 px-4 py-2 text-xs font-semibold tracking-widest uppercase rounded-sm transition-colors"
          >
            <ArrowClockwise size={14} weight="bold" /> Ververs
          </button>
        </div>

        {/* Company totals */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {KIND_META.map((m) => (
            <div
              key={m.key}
              className="neuro-card-bold p-5 bg-white"
              data-testid={`company-billing-total-${m.key}`}
            >
              <div className="flex items-center gap-2.5">
                <div
                  className="inline-flex h-10 w-10 items-center justify-center rounded-sm"
                  style={{ background: `${m.color}15`, color: m.color }}
                >
                  <m.icon size={20} weight="duotone" />
                </div>
                <div className="text-[11px] uppercase tracking-widest text-zinc-500">{m.title}</div>
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <div className="font-display text-3xl font-bold tracking-tight tabular-nums">
                  {totals[m.key].outstanding}
                </div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">nog te factureren</div>
              </div>
              <div className="mt-1 text-xs text-zinc-500 tabular-nums">
                {totals[m.key].invoiced} gefactureerd · {totals[m.key].total} totaal
              </div>
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-4 border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-zinc-500">Laden...</div>
        ) : rows.length === 0 ? (
          <div className="neuro-card p-12 text-center">
            <Receipt size={32} className="mx-auto text-zinc-300" />
            <h3 className="font-display text-lg font-bold mt-3 tracking-tight">Geen coaches gevonden</h3>
          </div>
        ) : (
          <div className="neuro-card-bold bg-white overflow-x-auto" data-testid="company-billing-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-zinc-900">
                  <th className="text-left py-3 px-4 font-display text-xs uppercase tracking-widest">Work coach</th>
                  {KIND_META.map((m) => (
                    <th
                      key={m.key}
                      className="text-left py-3 px-4 font-display text-xs uppercase tracking-widest"
                      style={{ color: m.color }}
                    >
                      {m.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.job_coach_id || "orphan"}
                    className="border-b border-zinc-100 hover:bg-zinc-50/50"
                    data-testid={`company-billing-row-${row.job_coach_id || "orphan"}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {row.is_bedrijfsadmin ? (
                          <UsersThree size={16} className="text-[#1E88E5]" weight="duotone" />
                        ) : (
                          <User size={16} className={row.job_coach_id ? "text-zinc-500" : "text-zinc-300"} weight="duotone" />
                        )}
                        <span className={row.job_coach_id ? "font-medium" : "italic text-zinc-500"}>
                          {row.coach_name}
                        </span>
                        {row.is_bedrijfsadmin && (
                          <span className="text-[10px] uppercase tracking-widest text-[#1E88E5] bg-[#1E88E5]/10 px-1.5 py-0.5 rounded-sm">
                            Bedrijfsadmin
                          </span>
                        )}
                      </div>
                    </td>
                    {KIND_META.map((m) => (
                      <td key={m.key} className="py-3 px-4">
                        <ReadonlyCell
                          counts={row[m.key]}
                          color={m.color}
                          testidPrefix={`company-cell-${row.job_coach_id || "orphan"}-${m.key}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function ReadonlyCell({ counts, color, testidPrefix }) {
  return (
    <div className="flex flex-col gap-0.5" data-testid={testidPrefix}>
      <div className="flex items-baseline gap-2">
        <div className="tabular-nums font-display text-lg font-bold tracking-tight">
          {counts.total}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-400">totaal</div>
      </div>
      <div className="flex items-center gap-3 text-xs">
        <span
          className="tabular-nums font-semibold"
          style={{ color }}
          data-testid={`${testidPrefix}-outstanding`}
        >
          {counts.outstanding} <span className="text-[10px] uppercase tracking-widest">nog</span>
        </span>
        <span className="tabular-nums text-zinc-500">
          {counts.invoiced} <span className="text-[10px] uppercase tracking-widest">✓</span>
        </span>
      </div>
    </div>
  );
}
